import { Box, Grid } from '@chakra-ui/react';
import {
  REVIEW_PROVIDER_TYPE_LABELS,
  useReviewCreationData,
  useReviewersSameSetting,
} from 'reducers/reviewCreationSlice';

import { ReceiverManager1 } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Accordion } from 'uiToolkitV1/Accordion';
import { AccordionButton, AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { Divider } from 'uiToolkitV1/Divider';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { TextHeader4 } from 'uiToolkitV1/Typography';

import { useGetReviewers } from '../../Hooks/dataHooks/reviewersDataHooks';
import { Detail, RenderDetails } from './RenderDetails';

const ReviewerTypes = ({ setting }) => {
  const details: Detail[] = [
    {
      key: 'Reviewer types:',
      value: setting.reviewerSettings.enabledProviderTypes.map((t) => REVIEW_PROVIDER_TYPE_LABELS[t]).join(', '),
    },
  ];

  return <RenderDetails details={details} />;
};

const PeerNominationSettings = ({ setting }) => {
  const hasPeer = setting.reviewerSettings.enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.PEER);
  const hasDLM = setting.reviewerSettings.enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.DLM);

  if (!hasPeer && !hasDLM) return null;

  const details: Detail[] = [
    {
      key: 'Peer/Dotted Line Manager nomination settings',
      value: '',
      isHeader: true,
    },
    ...(hasPeer
      ? [
          {
            key: 'Peer nominations automatically accepted:',
            value: setting.reviewerSettings.isAutoAcceptPeerNomination ? 'Yes' : 'No',
          },
        ]
      : []),
    ...(hasDLM
      ? [
          {
            key: 'Dotted Line Manager nominations automatically accepted:',
            value: setting.reviewerSettings.isAutoAcceptDlmNomination ? 'Yes' : 'No',
          },
        ]
      : []),
    {
      key: 'Who can nominate? (Apart from admins)',
      value: `${setting.reviewerSettings.isAllowPeerNominationBySelf ? 'Self, ' : ''}Manager`,
    },
    ...(hasPeer
      ? [
          {
            key: 'Number of Peer Nominations:',
            value: setting.reviewerSettings.maxPeers
              ? `${setting.reviewerSettings.minPeers} (Min) to ${setting.reviewerSettings.maxPeers} (Max)`
              : '',
          },
        ]
      : []),
  ];

  return <RenderDetails details={details} />;
};

const ManagerSettings = ({ setting }) => {
  const hasManager = setting.reviewerSettings.enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.MANAGER);

  if (!hasManager) return null;

  const details: Detail[] = [
    {
      key: 'Manager Settings',
      value: '',
      isHeader: true,
    },
    {
      key: "Allow manager review even if self review hasn't been submitted",
      value: setting.reviewerSettings.isAllowManagerFormFillingBeforeSelf ? 'Yes' : 'No',
    },
  ];

  return <RenderDetails details={details} />;
};

const SettingPanel = ({ settings, setting }) => {
  const settingSameAsFormId = useReviewersSameSetting({
    feedbackFormId: setting.feedbackFormId,
  });

  // ignoring lint error because we might need this code later
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const settingsSameAsCohort = settings.find((s) => s.feedbackFormId === settingSameAsFormId);

  return (
    <Accordion defaultIndex={0} allowMultiple allowToggle boxShadow="none !important" borderRadius="8px" mt={1}>
      <AccordionItem2 sxProps={{ borderBottom: 'none !important', p: '12px' }}>
        <AccordionButton p="20px 24px !important">
          <TextHeader4 bold>{setting?.feedbackFormName}</TextHeader4>
          {/* {settingsSameAsCohort && (
            <Flex ml={'auto'}>
              <TextBodyMedium mr={1}>Using same settings as</TextBodyMedium>
              <TextBodyMedium bold>{settingsSameAsCohort?.cohort?.name}</TextBodyMedium>
              <TooltipInfoV1 title="tooltip" ml={2} />
            </Flex>
          )} */}
        </AccordionButton>
        <AccordionPanel2 p={0}>
          <Box bg="white">
            <Divider borderColor="neutral.CFD7DF" mx={4} />
          </Box>
          <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="24px 24px 28px" bgColor="white">
            <ReviewerTypes setting={setting} />
            <PeerNominationSettings setting={setting} />
            <ManagerSettings setting={setting} />
          </Grid>
        </AccordionPanel2>
      </AccordionItem2>
    </Accordion>
  );
};

export const Reviewers = () => {
  const { reviewCycleId } = useReviewCreationData();
  const { data: { reviewersSettingsList = [] } = {}, isLoading } = useGetReviewers({
    reviewCycleId,
  });

  if (reviewersSettingsList?.length === 1) {
    const setting = reviewersSettingsList[0];

    return (
      <ExpandablePaper showLoader={isLoading}>
        <ExpandablePaperHeader
          title="Reviewers"
          leftIconComponent={<ReceiverManager1 boxSize="24px" color="icon.g2" />}
        />
        <ExpandablePaperBody>
          <Grid templateColumns="1fr 2fr" rowGap={8} columnGap={8} p="16px 8px 12px">
            <ReviewerTypes setting={setting} />
            <PeerNominationSettings setting={setting} />
            <ManagerSettings setting={setting} />
          </Grid>
        </ExpandablePaperBody>
      </ExpandablePaper>
    );
  }

  return (
    <ExpandablePaper showLoader={isLoading}>
      <ExpandablePaperHeader
        title="Reviewers"
        leftIconComponent={<ReceiverManager1 boxSize="24px" color="icon.g2" />}
        borderBottom="none"
        marginBottom={0}
      />
      <ExpandablePaperBody p="0 4px 4px 4px" bgColor="neutral.EBEFF3" borderRadius="8px">
        {reviewersSettingsList.map((setting) => (
          <SettingPanel settings={reviewersSettingsList} setting={setting} />
        ))}
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
